import { type AxiosProgressEvent } from "axios";

import { packNetAxios } from "@/lib/api-client";
import { type Campaign, type PrintImage, type StorageLocation, type ThumbnailResponse } from "@/schemas";
import { normalizeStorageLocation } from "@/utils/storage-location";

import { baseRoutes } from "./base-routes";

export type PrintConfig = {
	ColorProfiles: string[];
	ColorModes: string[];
	Colorants: string[];
};

export type TechnicalJob = {
	id: string;
	title: string;
};

const printImageApi = `${baseRoutes.printImage}/v1`;
const printImageApiEndpoint = `${baseRoutes.printImage}/v1/PrintImage`;
const printCampaignApiEndpoint = `${baseRoutes.printImage}/v1/campaign`;

export const PRINT_IMAGE_QUERY_KEY = ["print-images"] as const;
export const PRINT_CAMPAIGNS_QUERY_KEY = ["print-campaigns"] as const;

export async function getPrintConfig(iotEdgeDeviceId: string, iotHubName: string): Promise<PrintConfig> {
	const response = await packNetAxios.get<PrintConfig>(
		`${printImageApi}/PrintConfig?iotEdgeDeviceId=${encodeURIComponent(iotEdgeDeviceId)}&iotHubName=${encodeURIComponent(iotHubName)}`,
	);
	return response.data;
}

export async function getPrintImages(): Promise<PrintImage[]> {
	const response = await packNetAxios.get<PrintImage[]>(printImageApiEndpoint);
	return response.data;
}

export async function getPrintCampaigns(): Promise<Campaign[]> {
	const response = await packNetAxios.get<Campaign[]>(`${printCampaignApiEndpoint}/campaigns`);
	return response.data;
}

export async function createPrintCampaign(campaign: Campaign): Promise<void> {
	await packNetAxios.post(`${printCampaignApiEndpoint}/create`, campaign);
}

export async function updatePrintCampaign(campaignId: string, isActive: boolean): Promise<void> {
	await packNetAxios.put(`${printCampaignApiEndpoint}/setStatus/${encodeURIComponent(campaignId)}/${isActive}`, null);
}

export async function deletePrintCampaign(campaignId: string): Promise<void> {
	await packNetAxios.delete(`${printCampaignApiEndpoint}/delete/${encodeURIComponent(campaignId)}`);
}

export async function getPrintImage(id: string): Promise<PrintImage> {
	const response = await packNetAxios.get<PrintImage>(`${printImageApiEndpoint}/${id}/image`);
	return response.data;
}

export async function getPrintImageThumbnail(id: string): Promise<ThumbnailResponse> {
	const response = await packNetAxios.get<ThumbnailResponse>(`${printImageApiEndpoint}/thumbnail/${id}/image`);
	return response.data;
}

export async function getTechnicalJobs(iotHubName: string, iotEdgeDeviceId: string): Promise<TechnicalJob[]> {
	const response = await packNetAxios.get<TechnicalJob[]>(
		`${printImageApiEndpoint}/technicalJobs/${encodeURIComponent(iotHubName)}/${encodeURIComponent(iotEdgeDeviceId)}`,
	);
	return response.data;
}

export async function getPrintImageBlob(id: string): Promise<Blob> {
	const response = await packNetAxios.get<Blob>(`${printImageApiEndpoint}/${id}/rotatedImage`, {
		responseType: "blob",
	});
	return response.data;
}

interface CreatePrintImageOptions {
	formData: FormData;
	onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
}

export async function createPrintImage({ formData, onUploadProgress }: CreatePrintImageOptions): Promise<PrintImage> {
	const response = await packNetAxios.post<PrintImage>(`${printImageApiEndpoint}`, formData, {
		headers: {
			"Content-Type": "multipart/form-data",
		},
		timeout: 1200000,
		onUploadProgress,
	});
	return response.data;
}

interface UpdatePrintImageOptions {
	formData: FormData;
}

export async function updatePrintImage({ formData }: UpdatePrintImageOptions): Promise<PrintImage> {
	const response = await packNetAxios.put<PrintImage>(`${printImageApiEndpoint}`, formData, {
		headers: {
			"Content-Type": "multipart/form-data",
		},
		timeout: 1200000,
	});
	return response.data;
}

export async function deletePrintImage(id: string, storageLocation?: StorageLocation): Promise<void> {
	const normalizedLocation = storageLocation ? normalizeStorageLocation(storageLocation) : undefined;
	const url =
		normalizedLocation === "fiery" ? `${printImageApiEndpoint}/${id}/fiery` : `${printImageApiEndpoint}/${id}`;
	await packNetAxios.delete(url);
}

export async function getVersion(): Promise<string> {
	const response = await packNetAxios.get<string>(`${baseRoutes.printImage}/version`);
	return response.data;
}
